import type { BufferGeometry } from 'three';

/** Improve cap triangle aspect ratios without moving any boundary vertex.
 * Earcut's long fans around a dense, rounded nose notch are ill-conditioned
 * for interpolating the photo and glossy normals. Only internal diagonals
 * are flipped; the silhouette, holes, bevel and UV mapping stay in place. */
export function refineCapTriangles(geometry: BufferGeometry): void {
  if (geometry.index) throw new Error('Cap refinement requires unindexed extrusion');
  const p = geometry.getAttribute('position');
  const n = geometry.getAttribute('normal');
  const attributes = Object.values(geometry.attributes);
  for (const sign of [-1, 1]) {
    const points: Array<[number, number]> = [];
    const values: number[][][] = [];
    const ids = new Map<string, number>();
    const triangles: number[][] = [];
    const slots: number[] = [];
    for (let i = 0; i < p.count; i += 3) {
      if (![0, 1, 2].every(j => Math.abs(n.getZ(i + j) - sign) < 1e-6)) continue;
      const tri: number[] = [];
      for (let j = 0; j < 3; j++) {
        const v = i + j;
        const key = `${p.getX(v)},${p.getY(v)},${p.getZ(v)}`;
        let id = ids.get(key);
        if (id === undefined) {
          id = points.length; ids.set(key, id);
          points.push([p.getX(v), p.getY(v)]);
          values.push(attributes.map(a => Array.from({ length: a.itemSize }, (_, k) => a.getComponent(v, k))));
        }
        tri.push(id);
      }
      if (sign < 0) tri.reverse();
      triangles.push(tri); slots.push(i);
    }
    const area = (a: number, b: number, c: number): number => {
      const A = points[a]!, B = points[b]!, C = points[c]!;
      return (B[0]-A[0])*(C[1]-A[1])-(B[1]-A[1])*(C[0]-A[0]);
    };
    const quality = (t: number[]): number => {
      const [a,b,c] = t as [number,number,number];
      let sum = 0;
      for (const [u,v] of [[a,b],[b,c],[c,a]]) {
        const A=points[u!]!, B=points[v!]!;
        sum += (A[0]-B[0])**2+(A[1]-B[1])**2;
      }
      return area(a,b,c)**2 / Math.max(sum*sum,1e-30);
    };
    for (let pass = 0; pass < 40; pass++) {
      const edges = new Map<string, Array<[number,number,number,number]>>();
      triangles.forEach((t, id) => {
        for(let j=0;j<3;j++) {
          const u=t[j]!,v=t[(j+1)%3]!,a=t[(j+2)%3]!;
          const key=u<v?`${u},${v}`:`${v},${u}`;
          const entries=edges.get(key)??[]; entries.push([id,u,v,a]); edges.set(key,entries);
        }
      });
      const changed = new Set<number>();
      for(const adjacent of edges.values()) {
        if(adjacent.length!==2) continue; // constrained boundary
        const [i,u,v,a]=adjacent[0]!, [j,,,b]=adjacent[1]!;
        if(changed.has(i)||changed.has(j)||a===b) continue;
        const first=[a,b,v],second=[b,a,u];
        // Both new triangles must stay inside the original convex quad.
        if(area(a,b,v)<=1e-9||area(b,a,u)<=1e-9) continue;
        const old=Math.min(quality(triangles[i]!),quality(triangles[j]!));
        if(Math.min(quality(first),quality(second))<=old+1e-12) continue;
        triangles[i]=first;triangles[j]=second;changed.add(i);changed.add(j);
      }
      if(!changed.size) break;
    }
    triangles.forEach((tri,i)=>{
      const ordered=sign<0?[...tri].reverse():tri;
      ordered.forEach((id,j)=>attributes.forEach((a,k)=>{
        for(let c=0;c<a.itemSize;c++) a.setComponent(slots[i]!+j,c,values[id]![k]![c]!);
      }));
    });
  }
  attributes.forEach(a=>{ a.needsUpdate=true; });
}
